import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Crown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConectorVipConfirmation } from "@/components/conector/ConectorVipConfirmation";
import { ConectorVipWizard } from "@/components/conector/ConectorVipWizard";
import { AuthDialog } from "@/components/site/AuthDialog";
import { supabase } from "@/integrations/supabase/client";
import { useIsVip } from "@/hooks/use-user-roles";
import { getConectorDemandaData } from "@/lib/conector.functions";

export const Route = createFileRoute("/conector-vip/{-$id}")({
  validateSearch: (s: Record<string, unknown>) => ({
    editar: typeof s.editar === "string" ? s.editar : undefined,
  }),
  component: ConectorVipRoute,
});

function ConectorVipRoute() {
  const { id } = Route.useParams();
  const { editar } = Route.useSearch();
  const forceEdit = editar === "1";

  const hadIdOnMountRef = useRef(!!id);
  const initialIdRef = useRef<string | undefined>(id);

  const [estado, setEstado] = useState<string | null | undefined>(
    hadIdOnMountRef.current ? undefined : null,
  );
  const [sessionResolved, setSessionResolved] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);
  const { isVip, loading: vipLoading } = useIsVip();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setIsAuthed(!!data.session?.user);
      setSessionResolved(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setIsAuthed(!!session?.user);
      setSessionResolved(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!hadIdOnMountRef.current || !id) return;
    if (id !== initialIdRef.current) return;
    if (!sessionResolved) return;
    if (!isAuthed) {
      setEstado(null);
      return;
    }
    getConectorDemandaData({ data: { id } })
      .then((dem) => {
        setEstado(dem?.estado ?? null);
      })
      .catch(() => setEstado(null));
  }, [id, isAuthed, sessionResolved]);

  if (!sessionResolved || vipLoading || (hadIdOnMountRef.current && isAuthed && estado === undefined)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B1120]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B1120] px-4 py-10">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-card-foreground shadow-2xl">
          <div className="mb-4 text-center">
            <h1 className="text-lg font-semibold">Inicia sesión para usar el Conector VIP</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              El conector VIP busca entre locales off-market exclusivos para usuarios verificados.
            </p>
          </div>
          <AuthDialog inline />
        </div>
      </div>
    );
  }

  if (!isVip) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B1120] px-4 py-10">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center text-card-foreground shadow-2xl">
          <Crown className="mx-auto h-10 w-10 text-amber-600 dark:text-amber-400" />
          <h1 className="mt-4 text-xl font-bold">Tu cuenta no tiene acceso VIP</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Solicita el acceso VIP para usar el conector de locales off-market.
          </p>
          <Button asChild className="mt-6 bg-amber-600 text-white hover:bg-amber-700">
            <Link to="/vip">Solicitar acceso VIP</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (hadIdOnMountRef.current && estado === "iniciada" && !forceEdit && id) {
    return (
      <div className="min-h-screen bg-[#0B1120] px-4 py-10">
        <div className="mx-auto max-w-2xl rounded-2xl border bg-card p-8 sm:p-12">
          <ConectorVipConfirmation onEdit={() => { window.location.href = `/conector-vip/${id}?editar=1`; }} />
        </div>
      </div>
    );
  }

  return <ConectorVipWizard initialDemandaId={initialIdRef.current} />;
}
